import React, { useState, useEffect } from 'react';
import { X, Settings, Cookie, Shield, BarChart3, Target } from 'lucide-react';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  // Check if user has already made a choice
  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show banner after a short delay to avoid flashing
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Load saved preferences
      try {
        const savedPreferences = JSON.parse(consent);
        setPreferences(savedPreferences);
        applyCookieSettings(savedPreferences);
      } catch (error) {
        console.error('Error parsing cookie consent:', error);
      }
    }
  }, []);

  const applyCookieSettings = (settings) => {
    // Apply analytics settings
    if (settings.analytics && typeof window.gtag !== 'undefined') {
      // Enable Google Analytics if implemented
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    } else if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'analytics_storage': 'denied'
      });
    }

    // Apply marketing settings
    if (settings.marketing && typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'ad_storage': 'granted'
      });
    } else if (typeof window.gtag !== 'undefined') {
      window.gtag('consent', 'update', {
        'ad_storage': 'denied'
      });
    }

    // Store consent timestamp
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    applyCookieSettings(allAccepted);
    setIsVisible(false);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(necessaryOnly);
    localStorage.setItem('cookie-consent', JSON.stringify(necessaryOnly));
    applyCookieSettings(necessaryOnly);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    applyCookieSettings(preferences);
    setIsVisible(false);
  };

  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Cannot disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Notwendig',
      description: 'Diese Cookies sind für die Grundfunktionen der Website erforderlich.',
      icon: Shield,
      required: true,
      examples: 'Session-Cookies, Sicherheits-Cookies, Cookie-Einstellungen'
    },
    {
      id: 'functional',
      name: 'Funktional',
      description: 'Diese Cookies verbessern die Funktionalität und Personalisierung der Website.',
      icon: Settings,
      required: false,
      examples: 'Spracheinstellungen, Formular-Daten, Chat-Funktionen'
    },
    {
      id: 'analytics',
      name: 'Analytisch',
      description: 'Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren.',
      icon: BarChart3,
      required: false,
      examples: 'Google Analytics, Scroll-Tracking, Seitenaufrufe'
    },
    {
      id: 'marketing',
      name: 'Marketing',
      description: 'Diese Cookies werden verwendet, um Ihnen relevante Werbung zu zeigen.',
      icon: Target,
      required: false,
      examples: 'Social Media Tracking, Remarketing, Werbe-Cookies'
    }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm">
      <div className="fixed bottom-0 left-0 right-0 bg-white shadow-2xl border-t border-stone-200">
        <div className="max-w-6xl mx-auto p-6">
          {!showDetails ? (
            // Simple Banner
            <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="flex items-center space-x-3">
                <Cookie className="w-8 h-8 text-primary-600 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-stone-800 mb-2">
                    Cookie-Einstellungen
                  </h3>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    Wir verwenden Cookies, um Ihnen die beste Erfahrung auf unserer Website zu bieten. 
                    Einige sind notwendig, andere helfen uns, unsere Website zu verbessern und Ihnen 
                    relevante Inhalte zu zeigen.
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
                <button
                  onClick={() => setShowDetails(true)}
                  className="flex items-center justify-center space-x-2 px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors text-sm"
                >
                  <Settings className="w-4 h-4" />
                  <span>Einstellungen</span>
                </button>
                <button
                  onClick={handleAcceptNecessary}
                  className="px-4 py-2 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors text-sm"
                >
                  Nur Notwendige
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                >
                  Alle akzeptieren
                </button>
              </div>
            </div>
          ) : (
            // Detailed Settings
            <div className="max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <Cookie className="w-6 h-6 text-primary-600" />
                  <h3 className="text-xl font-semibold text-stone-800">
                    Cookie-Einstellungen anpassen
                  </h3>
                </div>
                <button
                  onClick={() => setShowDetails(false)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-stone-500" />
                </button>
              </div>

              <div className="space-y-6 mb-8">
                {cookieCategories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <div key={category.id} className="bg-stone-50 p-6 rounded-lg">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <Icon className="w-5 h-5 text-primary-600 mt-1" />
                          <div>
                            <h4 className="font-semibold text-stone-800 mb-1">
                              {category.name}
                              {category.required && (
                                <span className="ml-2 text-xs bg-stone-200 text-stone-600 px-2 py-1 rounded">
                                  Erforderlich
                                </span>
                              )}
                            </h4>
                            <p className="text-sm text-stone-600 mb-2">
                              {category.description}
                            </p>
                            <p className="text-xs text-stone-500">
                              <strong>Beispiele:</strong> {category.examples}
                            </p>
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences[category.id]}
                              disabled={category.required}
                              onChange={(e) => handlePreferenceChange(category.id, e.target.checked)}
                              className="sr-only peer"
                            />
                            <div className={`relative w-11 h-6 rounded-full peer transition-colors ${
                              category.required 
                                ? 'bg-stone-300 cursor-not-allowed' 
                                : preferences[category.id]
                                  ? 'bg-primary-600'
                                  : 'bg-stone-300'
                            } peer-focus:ring-4 peer-focus:ring-primary-300`}>
                              <div className={`absolute top-[2px] left-[2px] bg-white border rounded-full h-5 w-5 transition-transform ${
                                preferences[category.id] ? 'translate-x-full' : ''
                              }`}></div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-stone-200">
                <button
                  onClick={handleAcceptNecessary}
                  className="px-6 py-3 border border-stone-300 rounded-lg text-stone-700 hover:bg-stone-50 transition-colors"
                >
                  Nur Notwendige
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
                >
                  Einstellungen speichern
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  Alle akzeptieren
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-stone-200 text-xs text-stone-500">
                <p>
                  Sie können Ihre Einstellungen jederzeit ändern. Weitere Informationen finden Sie in unserer{' '}
                  <a href="/datenschutz" className="text-primary-600 hover:underline">
                    Datenschutzerklärung
                  </a>.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent; 